import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./ModalV2.module.css";
import closeIcon from "../icons/close.svg";
import { createPortal } from "react-dom";

const EXIT_ANIMATION_MS = 100;

const ModalV2 = ({
  open,
  setOpen,
  side,
  children,
  closeIconProps,
  className,
  draggable = false,
  closeOnOutsideClick = true,
  onClose = () => {},
}) => {
  const modalRef = useRef(null);
  const closeAnimationTimerRef = useRef(null);

  const [mounted, setMounted] = useState(false);
  const [shouldRender, setShouldRender] = useState(open);

  // Dragging State
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (open) {
      if (closeAnimationTimerRef.current) {
        clearTimeout(closeAnimationTimerRef.current);
        closeAnimationTimerRef.current = null;
      }
      setShouldRender(true);
      return;
    }

    if (!shouldRender) return;

    closeAnimationTimerRef.current = setTimeout(() => {
      setShouldRender(false);
      closeAnimationTimerRef.current = null;
    }, EXIT_ANIMATION_MS);

    return () => {
      if (closeAnimationTimerRef.current) {
        clearTimeout(closeAnimationTimerRef.current);
        closeAnimationTimerRef.current = null;
      }
    };
  }, [open, shouldRender]);

  useEffect(
    () => () => {
      if (closeAnimationTimerRef.current) {
        clearTimeout(closeAnimationTimerRef.current);
      }
    },
    []
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    setPosition({ x: 0, y: 0 });
    onClose();
  }, [setOpen, onClose]);

  const handleOverlayClick = (e) => {
    if (!closeOnOutsideClick) 
      return;
    
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // --- Escape Key Handler ---
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, handleClose]);

  // --- Dragging Handlers ---
  const handleMouseDown = (e) => {
    if (!draggable) return;

    // Prevent drag if clicking close button
    if (e.target.closest(`.${styles.closeButton}`)) return;

    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
    e.preventDefault();
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    },
    [isDragging, dragStart]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Global drag listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  if (!mounted || !shouldRender) return null;

  const modalContent = (
    <div
      className={[styles.Overlay, !open && styles.OverlayClosing]
        .filter(Boolean)
        .join(" ")}
      onClick={handleOverlayClick}
    >
      <div
        ref={modalRef}
        className={[
          styles.Modal,
          className,
          side && styles[side],
          !open && styles.ModalClosing,
        ]
          .filter(Boolean)
          .join(" ")}
        onMouseDown={handleMouseDown}
        {...(open ? { open: true } : {})}
        style={{
          // Apply drag styles
          ...(draggable
            ? {
                transform: `translate(${position.x}px, ${position.y}px)`,
                cursor: isDragging ? "grabbing" : "grab",
              }
            : {}),
        }}
      >
        <button
          onClick={handleClose}
          className={styles.closeButton}
          type="button"
        >
          <img
            src={closeIcon}
            alt="close"
            {...closeIconProps}
            className={[styles.closeIcon, closeIconProps?.className].join(" ")}
          />
        </button>
        <div className={styles.ModalContent}>{children}</div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ModalV2;
